import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, Button, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { TableColumn } from 'react-data-table-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRectangleList } from '@fortawesome/free-solid-svg-icons';
import { routeUserDashboardPolicyClaim, routeUserDashboardPolicyDetails } from '@components/UserDashboardRoute';
import UserBenefitScheduleModal from '@components/CorporateHealthQuoteBuyRoute/CorporateHealthQuoteBuyReviewPage/UserBenefitScheduleModal';
import { selectIsHospital } from '@components/Auth';
import { OrderableTypes } from '@models/Order';
import { CorporateUser } from '@models/User';
import { formatDate } from '@utils/datetime';
import { stateUpdate } from './action';
import { selectSearchCorporateMember } from './selector';
import { SearchFormFrontlineUser } from './SearchFormFrontlineUser/view';
import { SearchFormHospitalUser } from './SearchFormHospitalUser/view';

export const useColumns = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useMemo(
    () =>
      [
        {
          cell: (row) => (
            <Button
              variant="outline-primary"
              size="sm"
              as={Link}
              to={routeUserDashboardPolicyDetails({
                orderableType: OrderableTypes.CorporateHealth,
                orderableId: Number(row.corporateHealthOrder.id),
              })}
            >
              {t('common:policyDetails')}
            </Button>
          ),
          width: '150px',
          button: 'true',
          center: 'true',
        },
        {
          cell: (row) => (
            <Button
              as={Link}
              to={routeUserDashboardPolicyClaim(
                { orderableType: OrderableTypes.CorporateHealth, orderableId: Number(row.corporateHealthOrder.id) },
                { username: row.username },
              )}
              variant="outline-primary"
              size="sm"
              style={{ width: '80px' }}
            >
              {t('common:claim')}
            </Button>
          ),
          button: 'true',
          center: 'true',
        },
        {
          cell: (row) => (
            <Button
              variant="outline-primary"
              onClick={() => {
                dispatch(stateUpdate({ showBenefitScheduleDialog: true, selectedCorporateUser: row }));
              }}
              size="sm"
              style={{ width: '80px' }}
            >
              <FontAwesomeIcon icon={faRectangleList} />
            </Button>
          ),
          button: 'true',
          center: 'true',
        },
        {
          name: 'Username',
          selector: (row) => row.username,
          width: '120px',
        },
        {
          name: 'Family',
          selector: (row) => row.familyFullName,
          width: '150px',
        },
        {
          name: 'Name',
          selector: (row) => row.name,
          width: '150px',
        },
        {
          name: 'Relationship',
          selector: (row) => row.relationshipType,
          width: '150px',
        },
        {
          name: 'Policy number',
          selector: (row) => row.corporateHealthOrder.policyNumber,
          width: '180px',
        },
        {
          name: 'Date of birth',
          selector: (row) => row.dob,
          width: '150px',
        },
        {
          name: 'Gender',
          selector: (row) => row.gender,
        },
        {
          name: 'Status',
          cell: (row) => (
            <Badge bg={row.active ? 'success' : 'secondary'}>
              {row.active ? t('common:active') : t('common:inactive')}
            </Badge>
          ),
        },
        {
          name: 'Effective date',
          selector: (row) => row.startedAt,
          format: (row) => formatDate(row.startedAt),
          width: '150px',
        },
        {
          name: 'Expiry date',
          selector: (row) => row.endedAt,
          format: (row) => formatDate(row.endedAt),
          width: '150px',
        },
      ] as TableColumn<CorporateUser>[],
    [],
  );
};

const SearchCorporateMember = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isHospital = useSelector(selectIsHospital);
  const { showBenefitScheduleDialog, selectedCorporateUser } = useSelector(selectSearchCorporateMember);

  return (
    <Card>
      <Card.Header className="fs-5 fw-bold">{t('common:searchCorporateMember')}</Card.Header>
      <Card.Body>{isHospital ? <SearchFormHospitalUser /> : <SearchFormFrontlineUser />}</Card.Body>

      {showBenefitScheduleDialog && selectedCorporateUser && (
        <UserBenefitScheduleModal
          {...{ id: Number(selectedCorporateUser.corporateHealthOrder.id), user: selectedCorporateUser }}
          show={showBenefitScheduleDialog}
          onHide={() => {
            dispatch(stateUpdate({ showBenefitScheduleDialog: false, selectedCorporateUser: null }));
          }}
        />
      )}
    </Card>
  );
};

export { SearchCorporateMember };
